import { useState } from "react";
import { MapPin, Briefcase, Clock, DollarSign, CheckCircle, ArrowRight, Send, TrendingUp, Heart } from "lucide-react";
import axios from "axios";
import { usePageMeta } from "../hooks/usePageMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const perks = [
  { icon: DollarSign, title: "$30K to $100K+ Potential", desc: "Strong earning potential with a commission based structure that rewards your hustle." },
  { icon: Clock, title: "Weekends & Holidays Off", desc: "Total flexibility. You set your hours. No weekends, no holidays." },
  { icon: TrendingUp, title: "True Schedule Freedom", desc: "Control your time and your income. You decide when and how you work." },
  { icon: Heart, title: "Meaningful Work", desc: "Help businesses and their employees access better, more affordable benefits." },
];

const responsibilities = [
  "Meet with business owners & executives",
  "Present cost saving employee benefit solutions",
  "Build relationships in a B2B environment",
  "Control your schedule and your income",
  "Help HR managers and small business owners improve their employee benefits",
  "Reduce costs, risks, and admin time for your clients",
];

const qualifications = [
  "Willing to obtain ND Life & Health License",
  "High school diploma or equivalent",
  "Valid driver's license & reliable transportation",
  "Cell phone",
  "Able to pass a background check",
];

const LIST_ICON_SIZE = 17;
const initialForm = { name: "", email: "", phone: "", experience: "", message: "" };
const inputClass = "w-full rounded-lg border border-gray-200 bg-white px-4 py-3 text-sm text-[#0F172A] placeholder-gray-400 focus:outline-none focus:border-[#2EC4C4] focus:ring-1 focus:ring-[#2EC4C4] transition-colors";

const HeroSection = () => (
  <section data-testid="careers-hero" className="bg-[#1A2744] py-16 lg:py-24">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <h1 className="font-bold text-4xl sm:text-5xl text-white mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
        Join Our Team
      </h1>
      <p className="text-gray-300 text-lg max-w-2xl mx-auto">
        Build a rewarding career helping small businesses across North Dakota offer better employee benefits.
      </p>
    </div>
  </section>
);

const PerkCard = ({ perk }) => (
  <div className="bg-[#F8FAFC] rounded-2xl p-6 border border-gray-100 hover:shadow-md hover:-translate-y-1 transition-all text-center">
    <div className="w-12 h-12 bg-[#EAF9F9] rounded-xl flex items-center justify-center mx-auto mb-4">
      <perk.icon size={24} className="text-[#2EC4C4]" />
    </div>
    <h3 className="font-bold text-base text-[#1A2744] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{perk.title}</h3>
    <p className="text-[#475569] text-sm leading-relaxed">{perk.desc}</p>
  </div>
);

const PerksSection = () => (
  <section data-testid="careers-perks" className="py-20 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <h2 className="font-bold text-3xl lg:text-4xl text-[#1A2744] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Why Work With Viall Business Solutions?
        </h2>
        <p className="text-[#475569] text-lg max-w-2xl mx-auto">
          We're a growing team that values hard work, integrity, and genuine care for our clients and each other.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {perks.map((perk) => <PerkCard key={perk.title} perk={perk} />)}
      </div>
    </div>
  </section>
);

const JobHeader = () => (
  <div className="bg-[#1A2744] p-8">
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <div>
        <p className="text-[#2EC4C4] font-bold text-lg mb-1" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Earn $30K to $100K+ | Weekends & Holidays Off | Flexible Schedule
        </p>
        <h2 className="font-bold text-2xl text-white mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Business Sales Representative / Broker
        </h2>
        <div className="flex flex-wrap gap-4 text-gray-300 text-sm">
          <span className="flex items-center space-x-1"><MapPin size={14} /><span>Minot, ND</span></span>
          <span className="flex items-center space-x-1"><Clock size={14} /><span>Full Time</span></span>
          <span className="flex items-center space-x-1"><Briefcase size={14} /><span>Commission Based</span></span>
        </div>
      </div>
      <span className="inline-block bg-[#2EC4C4] text-white text-sm font-semibold px-4 py-2 rounded-full whitespace-nowrap">
        Now Hiring
      </span>
    </div>
  </div>
);

const JobBulletList = ({ title, items, Icon }) => (
  <div>
    <h3 className="font-bold text-lg text-[#1A2744] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>{title}</h3>
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item} className="flex items-start space-x-3">
          <Icon size={LIST_ICON_SIZE} className="text-[#2EC4C4] flex-shrink-0 mt-0.5" />
          <span className="text-[#475569] text-sm">{item}</span>
        </li>
      ))}
    </ul>
  </div>
);

const JobListingSection = () => (
  <section data-testid="job-listing" className="py-20 bg-[#F8FAFC]">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <JobHeader />
        <div className="p-8 space-y-8">
          <div>
            <h3 className="font-bold text-lg text-[#1A2744] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>About the Role</h3>
            <p className="text-[#475569] leading-relaxed">
              We help HR managers and small business owners improve their employee benefits while reducing costs, risks, and admin time. We're looking for driven, professional candidates who want true freedom and real income growth.
            </p>
          </div>
          <JobBulletList title="What You'll Do" items={responsibilities} Icon={CheckCircle} />
          <JobBulletList title="Requirements" items={qualifications} Icon={ArrowRight} />
        </div>
      </div>
    </div>
  </section>
);

const ApplicationSuccess = () => (
  <div data-testid="application-success" className="text-center py-12 bg-[#F8FAFC] rounded-2xl border border-gray-100">
    <div className="w-16 h-16 bg-[#EAF9F9] rounded-full flex items-center justify-center mx-auto mb-4">
      <CheckCircle size={32} className="text-[#2EC4C4]" />
    </div>
    <h3 className="font-bold text-xl text-[#1A2744] mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>Application Received!</h3>
    <p className="text-[#475569]">Thanks for your interest! We'll review your application and reach out soon.</p>
  </div>
);

const ApplicationForm = ({ form, status, onChange, onSubmit }) => (
  <form onSubmit={onSubmit} data-testid="application-form" className="bg-[#F8FAFC] rounded-2xl p-8 border border-gray-100 space-y-4">
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <label className="block text-sm font-medium text-[#1A2744] mb-1.5">Full Name <span className="text-[#2EC4C4]">*</span></label>
        <input type="text" name="name" required value={form.name} onChange={onChange} data-testid="apply-input-name" placeholder="John Smith" className={inputClass} />
      </div>
      <div>
        <label className="block text-sm font-medium text-[#1A2744] mb-1.5">Email Address <span className="text-[#2EC4C4]">*</span></label>
        <input type="email" name="email" required value={form.email} onChange={onChange} data-testid="apply-input-email" placeholder="john@email.com" className={inputClass} />
      </div>
    </div>
    <div>
      <label className="block text-sm font-medium text-[#1A2744] mb-1.5">Phone Number <span className="text-[#2EC4C4]">*</span></label>
      <input type="tel" name="phone" required value={form.phone} onChange={onChange} data-testid="apply-input-phone" placeholder="(555) 000 0000" className={inputClass} />
    </div>
    <div>
      <label className="block text-sm font-medium text-[#1A2744] mb-1.5">Relevant Experience</label>
      <input type="text" name="experience" value={form.experience} onChange={onChange} data-testid="apply-input-experience" placeholder="e.g. 3 years in insurance sales, licensed agent..." className={inputClass} />
    </div>
    <div>
      <label className="block text-sm font-medium text-[#1A2744] mb-1.5">Why do you want to join Viall Business Solutions? <span className="text-[#2EC4C4]">*</span></label>
      <textarea name="message" required rows={4} value={form.message} onChange={onChange} data-testid="apply-input-message" placeholder="Tell us a little about yourself and why you're interested..." className={`${inputClass} resize-none`} />
    </div>
    {status === "error" && (
      <p className="text-red-600 text-sm bg-red-50 px-4 py-3 rounded-lg">Something went wrong. Please email us directly at office@viallbusiness.com.</p>
    )}
    <button type="submit" disabled={status === "loading"} data-testid="apply-submit-btn" className="w-full bg-[#2EC4C4] hover:bg-[#25A0A0] disabled:opacity-60 text-white font-semibold rounded-lg px-8 py-4 text-base transition-all shadow-md hover:shadow-lg flex items-center justify-center space-x-2">
      {status === "loading" ? <span>Submitting...</span> : <><Send size={18} /><span>Submit Application</span></>}
    </button>
  </form>
);

const ApplicationFormSection = () => {
  const [form, setForm] = useState(initialForm);
  const [status, setStatus] = useState(null);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        business_name: `JOB APPLICATION: Business Sales Rep / Broker`,
        message: `Experience: ${form.experience}\n\n${form.message}`,
      });
      setStatus("success");
      setForm(initialForm);
    } catch {
      setStatus("error");
    }
  };

  return (
    <section data-testid="application-form-section" className="py-20 bg-white">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="font-bold text-3xl text-[#1A2744] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>Apply Now</h2>
          <p className="text-[#475569]">Interested in joining the team? Fill out the form below and we'll be in touch.</p>
        </div>
        {status === "success"
          ? <ApplicationSuccess />
          : <ApplicationForm form={form} status={status} onChange={handleChange} onSubmit={handleSubmit} />}
      </div>
    </section>
  );
};

export default function CareersPage() {
  usePageMeta({
    title: "Careers | Business Sales Representative & Broker Jobs in North Dakota",
    description: "Join the Viall Business Solutions team. Now hiring Business Sales Representatives and Brokers in North Dakota. $30K to $100K+ potential, flexible schedule, weekends off.",
    path: "/careers",
  });
  return (
    <div className="pt-16 lg:pt-20">
      <HeroSection />
      <PerksSection />
      <JobListingSection />
      <ApplicationFormSection />
    </div>
  );
}
